import React, { useState, useRef } from 'react';
import './Map.css';
import ColorPicker from './ColorPicker';

// Define tool types
export const TOOLS = {
  PENCIL: 'pencil',
  ERASER: 'eraser',
  WALL: 'wall',
  TOKEN: 'token',
  SELECT: 'select',
  POLYGON: 'polygon',
  RECTANGLE: 'rectangle',
  CIRCLE: 'circle'
};

const DrawingTools = ({ onToolChange, activeTool, toolOptions, onOptionChange, isNonWallLayerActive = false, onUnlockRequest, isDrawingUnlocked = false }) => {
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [colorPickerAnchor, setColorPickerAnchor] = useState(null);
  const colorButtonRef = useRef(null);

  const handleToolClick = (tool) => {
    // If trying to use a drawing tool on non-wall layer, show unlock request
    if (isNonWallLayerActive && tool !== TOOLS.SELECT) {
      onUnlockRequest?.(tool);
      return;
    }
    onToolChange(tool);
  };
  
  const handleOptionChange = (option, e) => {
    const value = e.target.type === 'range' ? parseInt(e.target.value) : e.target.value;
    onOptionChange(option, value);
  };

  const handleColorButtonClick = (e) => {
    e.preventDefault();
    setColorPickerAnchor(e.currentTarget);
    setShowColorPicker(true);
  };

  const handleColorChange = (newColor) => {
    // Update color for all drawing tools (sync across tools)
    onOptionChange('color', newColor);
  };

  const handleColorPickerClose = () => {
    setShowColorPicker(false);
    setColorPickerAnchor(null);
  };

  return (
    <div className="drawing-tools">
      <div className="tool-group">
        <h3>Drawing Tools</h3>
        <div className="tool-buttons">
          <button 
            className={`tool-button ${activeTool === TOOLS.SELECT ? 'active' : ''}`}
            onClick={() => handleToolClick(TOOLS.SELECT)}
            title="Select Tool"
          >
            <i className="tool-icon select-icon">&#9733;</i>
          </button>
          
          <button 
            className={`tool-button ${activeTool === TOOLS.PENCIL ? 'active' : ''} ${isNonWallLayerActive ? 'locked' : ''}`}
            onClick={() => handleToolClick(TOOLS.PENCIL)}
            title={isNonWallLayerActive ? "Locked - Click to unlock drawing on image layer" : "Pencil Tool"}
          >
            <i className="tool-icon pencil-icon">&#9998;</i>
            {isNonWallLayerActive && <i className="fas fa-lock lock-icon"></i>}
          </button>
          
          <button 
            className={`tool-button ${activeTool === TOOLS.ERASER ? 'active' : ''} ${isNonWallLayerActive ? 'locked' : ''}`}
            onClick={() => handleToolClick(TOOLS.ERASER)}
            title={isNonWallLayerActive ? "Locked - Click to unlock drawing on image layer" : "Eraser Tool"}
          >
            <i className="tool-icon eraser-icon">&#9003;</i>
            {isNonWallLayerActive && <i className="fas fa-lock lock-icon"></i>}
          </button>
          
          <button 
            className={`tool-button ${activeTool === TOOLS.WALL ? 'active' : ''} ${isNonWallLayerActive ? 'locked' : ''}`}
            onClick={() => handleToolClick(TOOLS.WALL)}
            title={isNonWallLayerActive ? "Locked - Click to unlock drawing on image layer" : "Wall Tool"}
          >
            <i className="fas fa-grip-lines tool-icon wall-icon"></i>
            {isNonWallLayerActive && <i className="fas fa-lock lock-icon"></i>}
          </button>
          
          <button 
            className={`tool-button ${activeTool === TOOLS.TOKEN ? 'active' : ''} ${isNonWallLayerActive ? 'locked' : ''}`}
            onClick={() => handleToolClick(TOOLS.TOKEN)}
            title={isNonWallLayerActive ? "Locked - Click to unlock drawing on image layer" : "Token Tool"}
          >
            <i className="fas fa-street-view tool-icon token-icon"></i>
            {isNonWallLayerActive && <i className="fas fa-lock lock-icon"></i>}
          </button>
          
          <button
            className={`tool-button ${activeTool === TOOLS.POLYGON ? 'active' : ''} ${isNonWallLayerActive ? 'locked' : ''}`}
            onClick={() => handleToolClick(TOOLS.POLYGON)}
            title={isNonWallLayerActive ? "Locked - Click to unlock drawing on image layer" : "Polygon Tool"}
          >
            <i className="tool-icon polygon-icon">&#11042;</i>
            {isNonWallLayerActive && <i className="fas fa-lock lock-icon"></i>}
          </button>

          <button
            className={`tool-button ${activeTool === TOOLS.RECTANGLE ? 'active' : ''} ${isNonWallLayerActive ? 'locked' : ''}`}
            onClick={() => handleToolClick(TOOLS.RECTANGLE)}
            title={isNonWallLayerActive ? "Locked - Click to unlock drawing on image layer" : "Rectangle Tool"}
          >
            <i className="tool-icon rectangle-icon">&#9645;</i>
            {isNonWallLayerActive && <i className="fas fa-lock lock-icon"></i>}
          </button>

          <button
            className={`tool-button ${activeTool === TOOLS.CIRCLE ? 'active' : ''} ${isNonWallLayerActive ? 'locked' : ''}`}
            onClick={() => handleToolClick(TOOLS.CIRCLE)}
            title={isNonWallLayerActive ? "Locked - Click to unlock drawing on image layer" : "Circle Tool"}
          >
            <i className="tool-icon circle-icon">&#9711;</i>
            {isNonWallLayerActive && <i className="fas fa-lock lock-icon"></i>}
          </button>
        </div>
      </div>
      
      <div className="tool-options">
        {activeTool === TOOLS.PENCIL && (
          <div className="pencil-options" style={{ display: 'flex', alignItems: 'center' }}>
            <label style={{ marginRight: '10px' }} title="Thickness">
              <i className="fas fa-arrows-alt-v" style={{ marginRight: '5px' }}></i>
              <input 
                type="range" 
                min="1" 
                max="20" 
                value={toolOptions.brushSize} 
                onChange={(e) => handleOptionChange('brushSize', e)}
              />
            </label>
            <div>
              <button
                ref={colorButtonRef}
                onClick={handleColorButtonClick}
                className="color-picker-button"
                title="Select Color"
                style={{
                  backgroundColor: toolOptions.color,
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  border: '2px solid #ccc',
                  cursor: 'pointer',
                  padding: 0,
                  margin: 0
                }}
              />
            </div>
          </div>
        )}
        
        {activeTool === TOOLS.ERASER && (
          <div className="eraser-options">
            <label>
              Size:
              <input 
                type="range" 
                min="5" 
                max="50" 
                value={toolOptions.eraserSize} 
                onChange={(e) => handleOptionChange('eraserSize', e)}
              />
            </label>
          </div>
        )}
        
        {activeTool === TOOLS.WALL && (
          <div className="wall-options" style={{ display: 'flex', alignItems: 'center' }}>
            <label style={{ marginRight: '10px' }} title="Thickness">
              <i className="fas fa-arrows-alt-v" style={{ marginRight: '5px' }}></i>
              <input 
                type="range" 
                min="1" 
                max="10" 
                value={toolOptions.wallThickness} 
                onChange={(e) => handleOptionChange('wallThickness', e)}
              />
            </label>
            <div>
              <button
                onClick={handleColorButtonClick}
                className="color-picker-button"
                title="Select Color"
                style={{
                  backgroundColor: toolOptions.color,
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  border: '2px solid #ccc',
                  cursor: 'pointer',
                  padding: 0,
                  margin: 0
                }}
              />
            </div>
          </div>
        )}
        
        {activeTool === TOOLS.TOKEN && (
          <div className="token-options" style={{ display: 'flex', alignItems: 'center' }}>
            <label style={{ marginRight: '10px' }}>
              Size:
              <input 
                type="range" 
                min="20" 
                max="80" 
                value={toolOptions.tokenSize} 
                onChange={(e) => handleOptionChange('tokenSize', e)}
              />
            </label>
            <div>
              <button
                onClick={handleColorButtonClick}
                className="color-picker-button"
                title="Select Color"
                style={{
                  backgroundColor: toolOptions.color,
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  border: '2px solid #ccc',
                  cursor: 'pointer',
                  padding: 0,
                  margin: 0
                }}
              />
            </div>
          </div>
        )}
        
        {(activeTool === TOOLS.POLYGON || activeTool === TOOLS.RECTANGLE || activeTool === TOOLS.CIRCLE) && (
          <div className="shape-options" style={{ display: 'flex', alignItems: 'center' }}>
            <label style={{ marginRight: '10px' }} title="Thickness">
              <i className="fas fa-arrows-alt-v" style={{ marginRight: '5px' }}></i>
              <input 
                type="range" 
                min="1" 
                max="10" 
                value={toolOptions.wallThickness}
                onChange={(e) => handleOptionChange('wallThickness', e)}
              />
            </label>
            
            <div style={{ marginRight: '10px' }}>
              <button
                onClick={handleColorButtonClick}
                className="color-picker-button"
                title="Select Color"
                style={{
                  backgroundColor: toolOptions.color,
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  border: '2px solid #ccc',
                  cursor: 'pointer',
                  padding: 0,
                  margin: 0
                }}
              />
            </div>

            <button
              className={`tool-button ${toolOptions.shapeBlockMode === 'standardWall' ? 'active' : ''}`}
              onClick={() => onOptionChange('shapeBlockMode', toolOptions.shapeBlockMode === 'standardWall' ? 'twoHitLOS' : 'standardWall')}
              title={toolOptions.shapeBlockMode === 'standardWall' ? "Make shape see-through (2-hit LOS)" : "Make shape opaque (1-hit LOS)"}
              style={{ minWidth: '80px' }}
            >
              Opaque
            </button>
          </div>
        )}
      </div>

      {/* Color Picker Component */}
      <ColorPicker
        isOpen={showColorPicker}
        onClose={handleColorPickerClose}
        initialColor={toolOptions.color}
        onChange={handleColorChange}
        anchorElement={colorPickerAnchor}
      />
    </div>
  );
};

export default DrawingTools; 